/**
 * Parent address and ZIP, as stored on `parents.address` / `parents.postal_code`.
 *
 * Both are optional. They are collected for Kaleido's Foster City Resident discount: ZIP 94404
 * qualifies a parent's children (see `utils/eligibilityDiscounts.ts`, which owns that rule).
 * This module only normalizes.
 *
 * The ZIP is stored as exactly five digits, which `parents_postal_code_check` enforces, so
 * checking a ZIP is an equality test. A ZIP+4 is accepted and cut to its first five digits.
 */

/** Longest address the inputs accept. Long enough for a unit number, short enough to catch a paste accident. */
export const ADDRESS_MAX_LENGTH = 200;

/** Room for `94404-1234` as typed. The stored value is always five digits. */
export const POSTAL_CODE_INPUT_MAX_LENGTH = 10;

export type PostalCodeResult = { ok: true; value: string | null } | { ok: false };

/**
 * Parse a ZIP as typed. An empty value is valid and means "clear it" (`null`). `94404`,
 * `94404-1234` and `944041234` all give `94404`. Anything else is invalid.
 */
export function parsePostalCode(raw: string): PostalCodeResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: null };
  if (!/^\d{5}(-?\d{4})?$/.test(trimmed)) return { ok: false };
  return { ok: true, value: trimmed.slice(0, 5) };
}

/** Trimmed address, or `null` when blank, so an emptied field clears the column rather than storing `''`. */
export function normalizeAddress(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed ? trimmed : null;
}

export const POSTAL_CODE_ERROR = 'Enter a 5-digit ZIP code.';

export interface AddressFields {
  address: string | null;
  postal_code: string | null;
}

/**
 * Only the address fields that differ from what was loaded, ready to spread into an update.
 *
 * `before` comes from a `select('*')`, so a missing column reads as `undefined` and counts as
 * `null`. An edit that leaves the address alone then sends neither key, and saving still works
 * against a database the patch has not reached yet.
 */
export function changedAddressFields(
  before: Partial<AddressFields>,
  next: AddressFields
): Partial<AddressFields> {
  const changes: Partial<AddressFields> = {};
  if (next.address !== (before.address ?? null)) changes.address = next.address;
  if (next.postal_code !== (before.postal_code ?? null)) changes.postal_code = next.postal_code;
  return changes;
}
